import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Filter, MapPin, Pencil, Plus, Search, Trash2,
  Truck as TruckIcon, X,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useOrgWarehouses } from '../../hooks/useOrgWarehouses';
import { useOrgTrucks, type TruckInput } from '../../hooks/useOrgTrucks';
import { useFleetDefaultTruckTypes, useFleetDefaultMakesModels, type FleetDefaultTruckType } from '../../hooks/useFleetDefaults';
import { Combobox } from '../../components/ui/Combobox';
import { CustomDropdown } from '../../components/ui/CustomDropdown';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import type { Warehouse, Truck, TruckStatus, TruckFuelType } from '../../types/admin';
// Note: TruckType (legacy enum) is no longer imported — truck types are now dynamic via fleet defaults

// ── Constants ───────────────────────────────────────────────────────────────

/** Legacy truck type labels for backward compatibility with existing data. */
const LEGACY_TRUCK_TYPE_LABELS: Record<string, string> = {
  cargo_van: 'Cargo Van',
  sprinter_van: 'Sprinter Van',
  '12ft_box': '12ft Box Truck',
  '16ft_box': '16ft Box Truck',
  '20ft_box': '20ft Box Truck',
  '24ft_box': '24ft Box Truck',
  '26ft_box': '26ft Box Truck',
  flatbed: 'Flatbed',
  other: 'Other',
};

/** Resolve a truck type value to a display label — checks fleet defaults first, falls back to legacy. */
function getTruckTypeLabel(type: string | undefined, truckTypes: FleetDefaultTruckType[]): string | undefined {
  if (!type) return undefined;
  const fromDefaults = truckTypes.find(t => t.id === type || t.name === type);
  if (fromDefaults) return fromDefaults.name;
  return LEGACY_TRUCK_TYPE_LABELS[type] ?? type;
}

const FUEL_TYPE_LABELS: Record<TruckFuelType, string> = {
  gas: 'Gas',
  diesel: 'Diesel',
  electric: 'Electric',
  hybrid: 'Hybrid',
};

const TRUCK_STATUS_LABELS: Record<TruckStatus, string> = {
  active: 'Active',
  maintenance: 'Maintenance',
  inactive: 'Inactive',
};

const TRUCK_STATUS_ORDER: Record<string, number> = { active: 0, maintenance: 1, inactive: 2 };

// ── Shared styles ───────────────────────────────────────────────────────────

const inputClass =
  'w-full h-11 px-3 rounded-lg border border-stone bg-white text-small text-midnight placeholder:text-slate focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors duration-150';
const labelClass = 'block text-[11px] font-bold uppercase tracking-widest text-slate';

function StatusBadge({ status }: { status: TruckStatus }) {
  const colors: Record<TruckStatus, string> = {
    active: 'bg-emerald-50 text-emerald-700',
    maintenance: 'bg-amber-50 text-amber-700',
    inactive: 'bg-stone text-slate',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${colors[status]}`}
    >
      {TRUCK_STATUS_LABELS[status]}
    </span>
  );
}

// ── Truck Form ──────────────────────────────────────────────────────────────

interface TruckFormProps {
  initial?: Truck;
  activeWarehouses: Warehouse[];
  truckTypes: FleetDefaultTruckType[];
  onClose: () => void;
  onSave: (input: TruckInput) => Promise<unknown>;
  onDelete?: () => void;
}

function TruckForm({ initial, activeWarehouses, truckTypes, onClose, onSave, onDelete }: TruckFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState(initial?.type ?? '');
  const [year, setYear] = useState(initial?.year?.toString() ?? '');
  const [make, setMake] = useState(initial?.make ?? '');
  const [model, setModel] = useState(initial?.model ?? '');
  const [licensePlate, setLicensePlate] = useState(initial?.licensePlate ?? '');
  const [capacity, setCapacity] = useState(initial?.capacity?.toString() ?? '');
  const [weightLimit, setWeightLimit] = useState(initial?.weightLimit?.toString() ?? '');
  const [currentLocation, setCurrentLocation] = useState(initial?.currentLocation ?? '');
  const [status, setStatus] = useState<TruckStatus | ''>(initial?.status ?? '');
  const [fuelType, setFuelType] = useState<TruckFuelType | ''>(initial?.fuelType ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fleet default makes/models, filtered by selected truck type
  const { items: allMakesModels } = useFleetDefaultMakesModels();
  const filteredMakesModels = useMemo(() => {
    if (!type) return allMakesModels;
    // type may be a truck type name or ID — resolve to ID for matching
    const tt = truckTypes.find(t => t.name === type || t.id === type);
    const typeId = tt?.id ?? type;
    return allMakesModels.filter(mm =>
      mm.compatibleTruckTypes.includes(typeId),
    );
  }, [allMakesModels, type, truckTypes]);

  // Distinct makes for the selected truck type
  const makeOptions = useMemo(() => {
    const seen = new Set<string>();
    return filteredMakesModels
      .filter(mm => { if (seen.has(mm.make)) return false; seen.add(mm.make); return true; })
      .map((mm, idx) => ({ id: idx, name: mm.make }));
  }, [filteredMakesModels]);

  // Models for the selected make + truck type
  const modelOptions = useMemo(() => {
    if (!make) return filteredMakesModels.map((mm, idx) => ({ id: idx, name: mm.model }));
    return filteredMakesModels
      .filter(mm => mm.make.toLowerCase() === make.toLowerCase())
      .map((mm, idx) => ({ id: idx, name: mm.model }));
  }, [filteredMakesModels, make]);

  // When truck type changes, auto-populate capacity and weight from defaults
  function handleTypeChange(newType: string) {
    setType(newType);
    if (newType) {
      const tt = truckTypes.find(t => t.id === newType || t.name === newType);
      if (tt) {
        // Only auto-populate if the fields are empty or this is a new truck
        if (!capacity || !initial) setCapacity(String(tt.defaultCapacity));
        if (!weightLimit || !initial) setWeightLimit(String(tt.defaultWeightLimit));
      }
    }
  }

  // When make changes, clear model if no longer compatible
  function handleMakeChange(newMake: string) {
    setMake(newMake);
    if (model) {
      const stillValid = filteredMakesModels.some(
        mm => mm.make.toLowerCase() === newMake.toLowerCase() && mm.model.toLowerCase() === model.toLowerCase(),
      );
      if (!stillValid) setModel('');
    }
  }

  // When model is selected, auto-populate fuel type from the matched make/model
  function handleModelChange(newModel: string) {
    setModel(newModel);
    const mm = filteredMakesModels.find(
      m => m.make.toLowerCase() === make.toLowerCase() && m.model.toLowerCase() === newModel.toLowerCase(),
    );
    if (mm && mm.fuelTypeDefaults.length === 1 && !fuelType) {
      setFuelType(mm.fuelTypeDefaults[0]);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Truck name is required.'); return; }
    setError('');
    setSaving(true);
    try {
      const input: TruckInput = { name: name.trim() };
      if (type) input.type = type;
      if (year) input.year = parseInt(year, 10);
      if (make.trim()) input.make = make.trim();
      if (model.trim()) input.model = model.trim();
      if (licensePlate.trim()) input.licensePlate = licensePlate.trim();
      if (capacity) input.capacity = parseFloat(capacity);
      if (weightLimit) input.weightLimit = parseFloat(weightLimit);
      if (currentLocation) input.currentLocation = currentLocation;
      if (status) input.status = status;
      if (fuelType) input.fuelType = fuelType;
      if (notes.trim()) input.notes = notes.trim();
      await onSave(input);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save truck.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div
        className="relative bg-white rounded-t-2xl px-4 pt-5 pb-8 max-h-[90vh] overflow-y-auto
                   md:rounded-2xl md:max-w-lg md:w-full md:max-h-[85vh] md:pb-6"
      >
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">
            {initial ? 'Edit Truck' : 'Add Truck'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && (
            <div className="px-4 py-3 rounded-lg bg-rose/10 border border-rose/30 text-small text-rose">
              {error}
            </div>
          )}

          {/* Name */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="tr-name" className={labelClass}>
              Truck Name <span className="text-rose">*</span>
            </label>
            <input
              id="tr-name"
              type="text"
              autoComplete="off"
              required
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Truck #1"
              className={inputClass}
            />
          </div>

          {/* Type */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="tr-type" className={labelClass}>Type</label>
            <CustomDropdown
              id="tr-type"
              value={type}
              onChange={v => handleTypeChange(v)}
              options={[
                { value: '', label: '—' },
                ...truckTypes.filter(t => t.active).map(t => ({ value: t.name, label: t.name })),
              ]}
              placeholder="—"
            />
          </div>

          {/* Make + Model */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="tr-make" className={labelClass}>Make</label>
              <Combobox
                id="tr-make"
                value={make}
                onChange={handleMakeChange}
                options={makeOptions}
                placeholder="e.g. Ford"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="tr-model" className={labelClass}>Model</label>
              <Combobox
                id="tr-model"
                value={model}
                onChange={handleModelChange}
                options={modelOptions}
                placeholder="e.g. E-450"
              />
            </div>
          </div>

          {/* Year + License Plate */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="tr-year" className={labelClass}>Year</label>
              <input
                id="tr-year"
                type="number"
                min={1990}
                max={new Date().getFullYear() + 2}
                value={year}
                onChange={e => setYear(e.target.value)}
                placeholder="e.g. 2022"
                className={inputClass}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="tr-plate" className={labelClass}>License Plate</label>
              <input
                id="tr-plate"
                type="text"
                value={licensePlate}
                onChange={e => setLicensePlate(e.target.value)}
                placeholder="ABC-1234"
                className={inputClass}
              />
            </div>
          </div>

          {/* Fuel Type */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="tr-fuel" className={labelClass}>Fuel Type</label>
            <CustomDropdown
              id="tr-fuel"
              value={fuelType}
              onChange={v => setFuelType(v as TruckFuelType | '')}
              options={[
                { value: '', label: '—' },
                ...Object.entries(FUEL_TYPE_LABELS).map(([k, v]) => ({ value: k, label: v })),
              ]}
              placeholder="—"
            />
          </div>

          {/* Capacity + Weight Limit */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="tr-cap" className={labelClass}>Capacity (cu ft)</label>
              <input
                id="tr-cap"
                type="number"
                min={0}
                value={capacity}
                onChange={e => setCapacity(e.target.value)}
                placeholder="1200"
                className={inputClass}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="tr-wt" className={labelClass}>Weight Limit (lbs)</label>
              <input
                id="tr-wt"
                type="number"
                min={0}
                value={weightLimit}
                onChange={e => setWeightLimit(e.target.value)}
                placeholder="10000"
                className={inputClass}
              />
            </div>
          </div>

          {/* Current Location + Status */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="tr-loc" className={labelClass}>Current Location</label>
              <CustomDropdown
                id="tr-loc"
                value={currentLocation}
                onChange={v => setCurrentLocation(v)}
                options={[
                  { value: '', label: '—' },
                  ...activeWarehouses.map(wh => ({ value: wh.id, label: wh.name })),
                  { value: 'on_job', label: 'On Job' },
                  { value: 'shop', label: 'Shop' },
                ]}
                placeholder="—"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="tr-status" className={labelClass}>Status</label>
              <CustomDropdown
                id="tr-status"
                value={status}
                onChange={v => setStatus(v as TruckStatus | '')}
                options={[
                  { value: '', label: '—' },
                  ...(Object.entries(TRUCK_STATUS_LABELS) as [TruckStatus, string][]).map(
                    ([k, v]) => ({ value: k, label: v }),
                  ),
                ]}
                placeholder="—"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="tr-notes" className={labelClass}>
              Notes{' '}
              <span className="text-slate font-normal normal-case tracking-normal">(optional)</span>
            </label>
            <textarea
              id="tr-notes"
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Any relevant details…"
              className="w-full px-3 py-2.5 rounded-lg border border-stone bg-white text-small text-midnight placeholder:text-slate resize-none focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors duration-150"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="h-11 rounded-lg bg-plum text-white text-[13px] font-semibold flex items-center justify-center gap-2 hover:bg-rose active:opacity-80 disabled:opacity-50 transition-colors duration-150 mt-1"
          >
            {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Truck'}
          </button>

          {initial && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="h-11 rounded-lg border border-rose/30 text-rose text-[13px] font-semibold flex items-center justify-center gap-2 hover:bg-rose/5 active:opacity-80 transition-colors duration-150"
            >
              <Trash2 size={14} /> Delete Truck
            </button>
          )}
        </form>
      </div>

    </div>
  );
}

// ── Truck Card ──────────────────────────────────────────────────────────────

function TruckCard({
  truck,
  warehouseMap,
  truckTypes,
  onEdit,
}: {
  truck: Truck;
  warehouseMap: Map<string, string>;
  truckTypes: FleetDefaultTruckType[];
  onEdit: () => void;
}) {
  const locationLabel = truck.currentLocation
    ? truck.currentLocation === 'on_job' ? 'On Job'
      : truck.currentLocation === 'shop' ? 'Shop'
        : warehouseMap.get(truck.currentLocation) ?? truck.currentLocation
    : null;

  const descParts: string[] = [];
  if (truck.year) descParts.push(String(truck.year));
  if (truck.make) descParts.push(truck.make);
  if (truck.model) descParts.push(truck.model);

  return (
    <button
      type="button"
      onClick={onEdit}
      className="w-full text-left bg-white border border-stone rounded-xl p-4 shadow-brand-sm flex flex-col gap-2 hover:border-plum/40 hover:shadow-brand-md active:bg-mist transition-all duration-150"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-plum/10 flex items-center justify-center mt-0.5">
          <TruckIcon size={17} className="text-plum" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-small text-midnight leading-snug">{truck.name}</p>
            {truck.status && <StatusBadge status={truck.status} />}
          </div>
          {descParts.length > 0 && (
            <p className="text-[13px] text-slate mt-0.5 leading-snug">{descParts.join(' ')}</p>
          )}
          {truck.type && (
            <p className="text-[11px] text-slate mt-0.5">{getTruckTypeLabel(truck.type, truckTypes)}</p>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
            {truck.licensePlate && (
              <span className="text-[12px] text-slate font-mono">{truck.licensePlate}</span>
            )}
            {locationLabel && (
              <span className="text-[12px] text-slate flex items-center gap-1">
                <MapPin size={11} /> {locationLabel}
              </span>
            )}
          </div>
          {truck.notes && (
            <p className="text-[13px] text-slate mt-1.5 leading-snug italic">{truck.notes}</p>
          )}
        </div>
        <Pencil size={14} className="shrink-0 text-slate mt-1" />
      </div>
    </button>
  );
}

// ── Inner Content ───────────────────────────────────────────────────────────

function ManageFleetContent({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const fleetEnabled = activeOrg?.featureFleetManagement ?? false;

  // Warehouses are fetched read-only — used for the truck location dropdown and card labels
  const { warehouses, loading: whLoading } = useOrgWarehouses(orgId);
  const { trucks, loading: trLoading, error, createTruck, updateTruck, deleteTruck } = useOrgTrucks(orgId);
  const { items: fleetTruckTypes } = useFleetDefaultTruckTypes();

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<TruckStatus | ''>('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<Truck | null>(null);
  const [deleting, setDeleting] = useState<Truck | null>(null);

  const hasActiveFilters = filterStatus !== '' || filterLocation !== '' || filterType !== '';

  const warehouseMap = useMemo(
    () => new Map(warehouses.map(w => [w.id, w.name])),
    [warehouses],
  );
  const activeWarehouses = useMemo(
    () => warehouses
      .filter(w => (w.status ?? 'active') === 'active')
      .sort((a, b) => a.name.localeCompare(b.name)),
    [warehouses],
  );

  const filtered = useMemo(() => {
    let list = [...trucks];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        t =>
          t.name.toLowerCase().includes(q) ||
          (t.year && String(t.year).includes(q)) ||
          (t.make && t.make.toLowerCase().includes(q)) ||
          (t.model && t.model.toLowerCase().includes(q)) ||
          (t.licensePlate && t.licensePlate.toLowerCase().includes(q)),
      );
    }
    if (filterStatus) list = list.filter(t => t.status === filterStatus);
    if (filterLocation) list = list.filter(t => t.currentLocation === filterLocation);
    if (filterType) list = list.filter(t => t.type === filterType);
    list.sort((a, b) => {
      const sa = TRUCK_STATUS_ORDER[a.status ?? 'active'] ?? 0;
      const sb = TRUCK_STATUS_ORDER[b.status ?? 'active'] ?? 0;
      if (sa !== sb) return sa - sb;
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [trucks, search, filterStatus, filterLocation, filterType]);

  const loading = whLoading || trLoading;

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-3xl md:mx-auto">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1.5 text-xs text-secondary-theme mb-2">
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="hover:text-plum transition-colors"
        >
          Admin Panel
        </button>
        <span>&gt;</span>
        <button
          type="button"
          onClick={() => navigate('/admin/operations')}
          className="hover:text-plum transition-colors"
        >
          Operations
        </button>
      </nav>

      <h1 className="font-display text-h1 text-midnight mb-1">Manage Fleet</h1>
      <p className="text-[13px] text-slate mb-6 leading-snug">
        Manage your trucks, capacity, and fleet availability. To manage warehouse locations,
        visit{' '}
        <button
          type="button"
          onClick={() => navigate('/admin/operations/manage-warehouses')}
          className="text-plum hover:underline font-medium"
        >
          Manage Warehouses
        </button>
        .
      </p>

      {loading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && (
        <>
          {/* Filters toggle */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate">
              Trucks ({filtered.length})
            </p>
            <button
              type="button"
              onClick={() => setShowFilters(prev => !prev)}
              className={`flex items-center gap-1 text-[12px] font-semibold transition-colors duration-150 ${
                hasActiveFilters ? 'text-plum' : 'text-slate hover:text-midnight'
              }`}
            >
              <Filter size={13} /> Filters{hasActiveFilters ? ' (active)' : ''}
            </button>
          </div>

          {/* Filter dropdowns */}
          {showFilters && (
            <div className="flex flex-wrap gap-2 mb-3">
              <CustomDropdown
                value={filterStatus}
                onChange={v => setFilterStatus(v as TruckStatus | '')}
                options={[
                  { value: '', label: 'All Statuses' },
                  ...(Object.entries(TRUCK_STATUS_LABELS) as [TruckStatus, string][]).map(
                    ([k, v]) => ({ value: k, label: v }),
                  ),
                ]}
                placeholder="All Statuses"
                fullWidth={false}
                minWidth="120px"
              />
              <CustomDropdown
                value={filterLocation}
                onChange={v => setFilterLocation(v)}
                options={[
                  { value: '', label: 'All Locations' },
                  ...activeWarehouses.map(wh => ({ value: wh.id, label: wh.name })),
                  { value: 'on_job', label: 'On Job' },
                  { value: 'shop', label: 'Shop' },
                ]}
                placeholder="All Locations"
                fullWidth={false}
                minWidth="120px"
              />
              <CustomDropdown
                value={filterType}
                onChange={v => setFilterType(v)}
                options={[
                  { value: '', label: 'All Types' },
                  ...fleetTruckTypes.filter(t => t.active).map(t => ({ value: t.name, label: t.name })),
                ]}
                placeholder="All Types"
                fullWidth={false}
                minWidth="120px"
              />
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={() => {
                    setFilterStatus('');
                    setFilterLocation('');
                    setFilterType('');
                  }}
                  className="h-9 px-2 text-[12px] text-rose hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {/* Search */}
          {trucks.length > 0 && (
            <div className="relative mb-3">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search trucks…"
                className="w-full h-10 pl-9 pr-3 rounded-lg border border-stone bg-white text-small text-midnight placeholder:text-slate focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors duration-150"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate hover:text-midnight"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          )}

          {/* Add button or upgrade callout */}
          {!fleetEnabled && trucks.length >= 1 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-4">
              <p className="text-[13px] font-semibold text-amber-800 mb-1">
                Truck limit reached
              </p>
              <p className="text-[12px] text-amber-700 leading-relaxed mb-2">
                Your current plan supports one truck. Upgrade your plan to add more trucks and
                other fleet management features.
              </p>
              <button
                type="button"
                onClick={() => navigate('/admin/company/billing/manage-plan')}
                className="flex items-center gap-1.5 text-[12px] font-semibold text-plum hover:underline"
              >
                Upgrade Plan <ArrowRight size={13} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="w-full h-11 rounded-xl border-2 border-dashed border-stone flex items-center justify-center gap-2 text-small font-semibold text-slate hover:border-plum hover:text-plum active:bg-mist transition-colors duration-150 mb-4"
            >
              <Plus size={16} /> Add Truck
            </button>
          )}

          {/* Error state */}
          {error && (
            <div className="flex flex-col items-center py-8 gap-2 text-center">
              <p className="text-small text-rose font-semibold">Failed to load trucks</p>
              <p className="text-small text-slate">{error}</p>
            </div>
          )}

          {/* Empty — nothing added yet */}
          {!error && trucks.length === 0 && (
            <div className="flex flex-col items-center py-12 gap-3 text-center">
              <div className="w-14 h-14 rounded-full bg-mist flex items-center justify-center">
                <TruckIcon size={24} className="text-slate" />
              </div>
              <p className="text-small text-slate">No trucks yet. Add one above.</p>
            </div>
          )}

          {/* Empty — search/filter returned nothing */}
          {!error && trucks.length > 0 && filtered.length === 0 && (
            <p className="text-small text-slate text-center py-8">
              No trucks match your search or filters.
            </p>
          )}

          {/* Truck list */}
          {!error && filtered.length > 0 && (
            <ul className="flex flex-col gap-3">
              {filtered.map(tr => (
                <li key={tr.id}>
                  <TruckCard truck={tr} warehouseMap={warehouseMap} truckTypes={fleetTruckTypes} onEdit={() => setEditing(tr)} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* ── Modals ──────────────────────────────────────────────── */}

      {showAdd && (
        <TruckForm
          activeWarehouses={activeWarehouses}
          truckTypes={fleetTruckTypes}
          onClose={() => setShowAdd(false)}
          onSave={createTruck}
        />
      )}

      {editing && (
        <TruckForm
          initial={editing}
          activeWarehouses={activeWarehouses}
          truckTypes={fleetTruckTypes}
          onClose={() => setEditing(null)}
          onSave={input => updateTruck(editing.id, input)}
          onDelete={() => {
            setDeleting(editing);
            setEditing(null);
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!deleting}
        title="Delete Truck"
        message={`Are you sure you want to delete "${deleting?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (deleting) deleteTruck(deleting.id);
          setDeleting(null);
        }}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}

// ── Page (guards org admin access) ───────────────────────────────────────────

export function ManageFleetPage() {
  const { adminOrg, loading } = useUserProfile();

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!adminOrg) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-3 text-center">
        <p className="text-small text-slate">You don't have org admin access.</p>
      </div>
    );
  }

  return <ManageFleetContent orgId={adminOrg.orgId} />;
}
